import { useEffect } from 'react';

/**
 * Keeps the phone awake for the duration of the call. Without it a candidate
 * who stops touching the screen watches it dim and lock mid-answer.
 *
 * The lock is dropped by the OS whenever the tab is hidden, so it has to be
 * reacquired on visibilitychange rather than just taken once.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        // Denied (low battery, unsupported). Nothing to do but carry on.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}
